import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    ClipboardList, ExternalLink, Search, FileText, Image as ImageIcon,
    Download, Eye, Calendar as CalendarIcon, Building2, User as UserIcon,
    Filter, X, Loader2, ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { studyFormsAPI } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'valnexo_token';

export default function FormsCEEPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [formType, setFormType] = useState('all');
    const [datePreset, setDatePreset] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedForm, setSelectedForm] = useState(null);
    const [showProspectPicker, setShowProspectPicker] = useState(false);
    const [prospects, setProspects] = useState([]);
    const [prospectQuery, setProspectQuery] = useState('');
    const [openingForm, setOpeningForm] = useState(false);

    const computePresetRange = (preset) => {
        const now = new Date();
        const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
        const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
        const fmt = (d) => d.toISOString();
        switch (preset) {
            case 'today': {
                return { from: fmt(startOfDay(now)), to: fmt(endOfDay(now)) };
            }
            case 'yesterday': {
                const y = new Date(now); y.setDate(y.getDate() - 1);
                return { from: fmt(startOfDay(y)), to: fmt(endOfDay(y)) };
            }
            case 'this_week': {
                const day = now.getDay() || 7; // lundi = 1, dimanche = 7
                const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
                return { from: fmt(startOfDay(monday)), to: fmt(endOfDay(now)) };
            }
            case 'this_month': {
                const start = new Date(now.getFullYear(), now.getMonth(), 1);
                return { from: fmt(startOfDay(start)), to: fmt(endOfDay(now)) };
            }
            case 'last_month': {
                const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const end = new Date(now.getFullYear(), now.getMonth(), 0);
                return { from: fmt(startOfDay(start)), to: fmt(endOfDay(end)) };
            }
            case 'this_year': {
                const start = new Date(now.getFullYear(), 0, 1);
                return { from: fmt(startOfDay(start)), to: fmt(endOfDay(now)) };
            }
            default:
                return { from: '', to: '' };
        }
    };

    const handlePresetChange = (preset) => {
        setDatePreset(preset);
        if (preset === 'custom') return; // garder les dates manuelles
        const { from, to } = computePresetRange(preset);
        setDateFrom(from ? from.slice(0, 10) : '');
        setDateTo(to ? to.slice(0, 10) : '');
        // Re-charger immédiatement
        setTimeout(() => loadForms(from, to), 50);
    };

    const loadForms = async (overrideFrom, overrideTo) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.append('q', search);
            if (formType && formType !== 'all') params.append('form_type', formType);
            const fFrom = overrideFrom !== undefined ? overrideFrom : dateFrom;
            const fTo = overrideTo !== undefined ? overrideTo : dateTo;
            if (fFrom) params.append('date_from', fFrom);
            if (fTo) params.append('date_to', fTo);
            const res = await api.get(`/forms/cee?${params}`);
            setForms(res.data.forms || []);
        } catch (err) {
            toast.error('Erreur lors du chargement des formulaires');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadForms(); }, []);

    const handleSearch = (e) => {
        e?.preventDefault();
        loadForms();
    };

    const resetFilters = () => {
        setSearch(''); setFormType('all'); setDatePreset('all'); setDateFrom(''); setDateTo('');
        setTimeout(() => loadForms('', ''), 50);
    };

    const openProspectPicker = async () => {
        setProspectQuery('');
        setShowProspectPicker(true);
        if (prospects.length === 0) {
            try {
                const res = await api.get('/prospects?page_size=100000');
                const list = Array.isArray(res.data) ? res.data : (res.data.prospects || []);
                setProspects(list);
            } catch (err) {
                toast.error('Erreur chargement prospects');
            }
        }
    };

    const filteredProspects = (() => {
        if (!prospectQuery.trim()) return prospects.slice(0, 100);
        const q = prospectQuery.toLowerCase().trim();
        const tokens = q.split(/\s+/).filter(Boolean);
        return prospects.filter(p => {
            const hay = [
                p.last_name, p.first_name, p.company_name,
                p.city, p.email, p.phone, p.id?.slice(0, 8),
            ].filter(Boolean).join(' ').toLowerCase();
            return tokens.every(t => hay.includes(t));
        });
    })();

    const handlePickerKeyDown = (e) => {
        if (e.key === 'Enter' && filteredProspects.length > 0 && !openingForm) {
            e.preventDefault();
            handlePickProspect(filteredProspects[0]);
        }
    };

    const handlePickProspect = async (prospect) => {
        setOpeningForm(true);
        try {
            const res = await studyFormsAPI.create(prospect.id, {});
            setShowProspectPicker(false);
            setOpeningForm(false);
            navigate(`/prospects/${prospect.id}/study/${res.data.id}`);
        } catch (err) {
            setOpeningForm(false);
            toast.error(err.response?.data?.detail || 'Erreur création du relevé');
        }
    };

    const downloadFile = async (cee_id, storage_path, filename) => {
        try {
            const res = await api.get(
                `/forms/cee/${cee_id}/file?path=${encodeURIComponent(storage_path)}`,
                { responseType: 'blob' },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'fichier';
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error('Erreur de téléchargement');
        }
    };

    const viewFile = (cee_id, storage_path) => {
        const token = localStorage.getItem(TOKEN_KEY);
        const url = `${API}/api/forms/cee/${cee_id}/file?path=${encodeURIComponent(storage_path)}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const objUrl = URL.createObjectURL(blob);
                window.open(objUrl, '_blank');
            })
            .catch(() => toast.error('Erreur ouverture fichier'));
    };

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
                            <ClipboardList className="w-7 h-7 text-blue-600" />
                            Formulaire d'Étude sur Place
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            Relevés CEE Tertiaire et Rénovation Globale d'Ampleur
                        </p>
                    </div>
                </div>

                {/* Section 1 — Présentation + bouton */}
                <Card className="border-blue-200 bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 overflow-hidden" data-testid="forms-cee-intro-card">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-1">
                                <h2 className="text-lg font-bold text-slate-900 mb-2">
                                    Démarrer un nouveau relevé d'étude sur place
                                </h2>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    Sélectionnez le prospect concerné : le relevé s'ouvre <strong>directement dans le CRM</strong>.
                                    Tout est <strong>enregistré en continu</strong> (même hors-ligne) — vous pouvez quitter et revenir
                                    autant de fois que nécessaire. À la validation, le <strong>PDF récapitulatif</strong> est généré et conservé.
                                </p>
                                <p className="text-xs text-slate-500 mt-3">
                                    Le relevé est rattaché au dossier : retrouvez-le aussi dans l'onglet « Chantier » du dossier, sous le pipeline.
                                </p>
                            </div>
                            <div className="flex-shrink-0">
                                <Button
                                    size="lg"
                                    onClick={openProspectPicker}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg gap-2"
                                    data-testid="open-form-btn"
                                >
                                    <ClipboardList className="w-5 h-5" />
                                    Nouveau relevé
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Section 2 — Filtres + Liste */}
                <Card data-testid="forms-cee-list-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <FileText className="w-5 h-5 text-slate-600" />
                            Historique des formulaires
                            <span className="text-sm font-normal text-slate-500 ml-2">
                                ({forms.length} {forms.length > 1 ? 'résultats' : 'résultat'})
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {/* Filtres */}
                        <form onSubmit={handleSearch} className="space-y-3 mb-5" data-testid="forms-cee-filters">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                                <div className="md:col-span-3">
                                    <div className="relative">
                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <Input
                                            placeholder="Recherche (nom, raison sociale, ville, SIREN...)"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            className="pl-9"
                                            data-testid="forms-cee-search-input"
                                        />
                                    </div>
                                </div>
                                <Select value={formType} onValueChange={setFormType}>
                                    <SelectTrigger data-testid="forms-cee-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous types</SelectItem>
                                        <SelectItem value="cee_etude">CEE Étude</SelectItem>
                                        <SelectItem value="reno_globale">Réno Globale</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={datePreset} onValueChange={handlePresetChange}>
                                    <SelectTrigger data-testid="forms-cee-period-filter">
                                        <CalendarIcon className="w-4 h-4 mr-1 text-slate-500" />
                                        <SelectValue placeholder="Période" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toutes les périodes</SelectItem>
                                        <SelectItem value="today">Aujourd'hui</SelectItem>
                                        <SelectItem value="yesterday">Hier</SelectItem>
                                        <SelectItem value="this_week">Cette semaine</SelectItem>
                                        <SelectItem value="this_month">Ce mois</SelectItem>
                                        <SelectItem value="last_month">Le mois dernier</SelectItem>
                                        <SelectItem value="this_year">Cette année</SelectItem>
                                        <SelectItem value="custom">Personnalisée</SelectItem>
                                    </SelectContent>
                                </Select>
                                {datePreset === 'custom' && (
                                    <>
                                        <Input
                                            type="date"
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            placeholder="Du"
                                            data-testid="forms-cee-date-from"
                                        />
                                        <Input
                                            type="date"
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            placeholder="Au"
                                            data-testid="forms-cee-date-to"
                                        />
                                    </>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button type="submit" size="sm" className="gap-2" data-testid="forms-cee-search-btn">
                                    <Filter className="w-4 h-4" /> Filtrer
                                </Button>
                                <Button type="button" variant="ghost" size="sm" onClick={resetFilters}>
                                    <X className="w-4 h-4 mr-1" /> Réinitialiser
                                </Button>
                            </div>
                        </form>

                        {/* Tableau */}
                        {loading ? (
                            <div className="space-y-2">
                                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                            </div>
                        ) : forms.length === 0 ? (
                            <div className="py-16 text-center text-slate-500">
                                <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                                <p className="text-sm">Aucun formulaire pour le moment</p>
                                <p className="text-xs text-slate-400 mt-1">
                                    Cliquez sur "Nouveau formulaire" pour démarrer le premier
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="forms-cee-table">
                                    <thead className="text-xs text-slate-500 uppercase border-b">
                                        <tr>
                                            <th className="text-left py-3 px-3 font-medium">Date</th>
                                            <th className="text-left py-3 px-3 font-medium">Raison sociale</th>
                                            <th className="text-left py-3 px-3 font-medium">Type</th>
                                            <th className="text-left py-3 px-3 font-medium">Prospect</th>
                                            <th className="text-left py-3 px-3 font-medium">Soumis par</th>
                                            <th className="text-center py-3 px-3 font-medium">Photos</th>
                                            <th className="text-right py-3 px-3 font-medium">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {forms.map(f => (
                                            <tr key={f.id} className="hover:bg-slate-50 transition-colors" data-testid={`forms-cee-row-${f.id}`}>
                                                <td className="py-3 px-3 text-slate-700 whitespace-nowrap">
                                                    {f.created_at ? format(new Date(f.created_at), 'dd/MM/yy HH:mm', { locale: fr }) : '-'}
                                                </td>
                                                <td className="py-3 px-3 font-medium text-slate-900">
                                                    {f.raison_sociale || <span className="text-slate-400">—</span>}
                                                </td>
                                                <td className="py-3 px-3">
                                                    <span className={`text-xs px-2 py-1 rounded-full ${f.form_type === 'reno_globale' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                        {f.form_type === 'reno_globale' ? 'Réno Globale' : 'CEE Étude'}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-3 text-slate-700">
                                                    {f.prospect_label || '—'}
                                                </td>
                                                <td className="py-3 px-3 text-slate-600">
                                                    {f.submitted_by_name || '—'}
                                                </td>
                                                <td className="py-3 px-3 text-center text-slate-600">
                                                    <span className="inline-flex items-center gap-1">
                                                        <ImageIcon className="w-3.5 h-3.5" />
                                                        {f.photos_count || 0}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-3 text-right">
                                                    <Button size="sm" variant="ghost" onClick={() => setSelectedForm(f)} data-testid={`view-form-${f.id}`}>
                                                        <Eye className="w-4 h-4 mr-1" /> Détail
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Modal Choix prospect */}
                <Dialog open={showProspectPicker} onOpenChange={setShowProspectPicker}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Sélectionner le prospect</DialogTitle>
                            <DialogDescription>
                                Choisissez le prospect auquel rattacher ce formulaire d'étude.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="relative mb-3">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Tapez un nom, société, ville, email..."
                                value={prospectQuery}
                                onChange={(e) => setProspectQuery(e.target.value)}
                                onKeyDown={handlePickerKeyDown}
                                className="pl-9"
                                data-testid="prospect-picker-search"
                                autoFocus
                            />
                            {prospects.length > 0 && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                                    {filteredProspects.length}/{prospects.length}
                                </div>
                            )}
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100 border rounded-lg">
                            {prospects.length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-400 flex items-center justify-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement des prospects...
                                </div>
                            ) : filteredProspects.length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-400">Aucun prospect trouvé</div>
                            ) : (
                                filteredProspects.slice(0, 200).map((p, idx) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => handlePickProspect(p)}
                                        disabled={openingForm}
                                        className={`w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center justify-between transition-colors disabled:opacity-50 ${idx === 0 && prospectQuery ? 'bg-blue-50/50' : ''}`}
                                        data-testid={`pick-prospect-${p.id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-slate-900 truncate">
                                                {p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Sans nom'}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-0.5 truncate">
                                                {[
                                                    p.company_name && `${p.first_name || ''} ${p.last_name || ''}`.trim(),
                                                    p.city,
                                                    p.email,
                                                ].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                                    </button>
                                ))
                            )}
                            {filteredProspects.length > 200 && (
                                <div className="px-4 py-2 text-xs text-center text-slate-400 bg-slate-50">
                                    {filteredProspects.length - 200} résultats supplémentaires — affinez votre recherche
                                </div>
                            )}
                        </div>
                        {prospectQuery && filteredProspects.length > 0 && !openingForm && (
                            <div className="text-xs text-slate-500 mt-2 text-center">
                                Appuyez sur <kbd className="px-1.5 py-0.5 bg-slate-100 rounded border text-[10px] font-mono">Entrée</kbd> pour sélectionner le premier résultat
                            </div>
                        )}
                        {openingForm && (
                            <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mt-2">
                                <Loader2 className="w-4 h-4 animate-spin" /> Génération du lien sécurisé...
                            </div>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Modal Détail formulaire */}
                <Dialog open={!!selectedForm} onOpenChange={(o) => !o && setSelectedForm(null)}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        {selectedForm && (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-blue-600" />
                                        {selectedForm.raison_sociale || 'Formulaire CEE'}
                                    </DialogTitle>
                                    <DialogDescription>
                                        Soumis le {selectedForm.created_at && format(new Date(selectedForm.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                                        {selectedForm.submitted_by_name && ` par ${selectedForm.submitted_by_name}`}
                                    </DialogDescription>
                                </DialogHeader>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                    {selectedForm.siren && <Info label="SIREN" value={selectedForm.siren} />}
                                    {selectedForm.siret && <Info label="SIRET" value={selectedForm.siret} />}
                                    {selectedForm.ville && <Info label="Ville" value={selectedForm.ville} />}
                                    {selectedForm.code_postal && <Info label="Code postal" value={selectedForm.code_postal} />}
                                    {selectedForm.adresse && <Info label="Adresse" value={selectedForm.adresse} />}
                                    {selectedForm.prospect_label && <Info label="Prospect" value={selectedForm.prospect_label} />}
                                    {selectedForm.external_form_id && <Info label="ID externe" value={selectedForm.external_form_id} />}
                                </div>

                                {/* PDF */}
                                {selectedForm.pdf?.storage_path && (
                                    <div className="border rounded-lg p-3 bg-slate-50">
                                        <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                            <FileText className="w-4 h-4 text-red-600" /> PDF récapitulatif
                                        </h3>
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => viewFile(selectedForm.id, selectedForm.pdf.storage_path)}>
                                                <Eye className="w-4 h-4 mr-1" /> Visualiser
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => downloadFile(selectedForm.id, selectedForm.pdf.storage_path, selectedForm.pdf.name)}>
                                                <Download className="w-4 h-4 mr-1" /> Télécharger
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Photos */}
                                {selectedForm.photos?.length > 0 && (
                                    <div className="border rounded-lg p-3 bg-slate-50">
                                        <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                            <ImageIcon className="w-4 h-4 text-emerald-600" /> Photos ({selectedForm.photos.length})
                                        </h3>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                            {selectedForm.photos.map((ph, idx) => (
                                                <div key={idx} className="relative group bg-white border rounded overflow-hidden">
                                                    <div className="aspect-square flex items-center justify-center bg-slate-100">
                                                        <ImageIcon className="w-8 h-8 text-slate-300" />
                                                    </div>
                                                    <div className="p-2 text-xs">
                                                        <div className="truncate font-medium text-slate-700">{ph.name}</div>
                                                        <div className="flex gap-1 mt-1">
                                                            <button onClick={() => viewFile(selectedForm.id, ph.storage_path)} className="text-blue-600 hover:underline">Voir</button>
                                                            <span className="text-slate-300">·</span>
                                                            <button onClick={() => downloadFile(selectedForm.id, ph.storage_path, ph.name)} className="text-blue-600 hover:underline">DL</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}

const Info = ({ label, value }) => (
    <div className="bg-slate-50 rounded p-2">
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-slate-900 font-medium">{value}</div>
    </div>
);
